import styled, { keyframes } from "styled-components";

/**
 * Rogyog.styles.js
 *
 * v3 — full redesign to match YogGoalAsanas.styles.js's design system
 * (same sage/gold/cream palette: #2c5940 / #3d6b4f sage, #c9a84c gold,
 * #2e1f0f ink, #faf6ee cream) and the new reference layout: centered
 * hero with feature badges → category pills → inline search + filter →
 * two-column (sub-category/video sidebar + player) → "More Practices"
 * carousel → bottom CTA banner.
 *
 * This is a from-scratch rewrite, same as YogGoalAsanas.styles.js was —
 * if anything else in the app imports individual exports from here,
 * double check before dropping this in, since names/shapes changed.
 * `Section`'s `id="rogYog"` anchor is preserved so existing links to
 * #rogYog keep working.
 */

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const Section = styled.section`
  background: #faf7f2;
  color: #2e1f0f;
  padding: 28px 24px 90px;

  @media (min-width: 900px) {
    padding: 32px 40px 110px;
  }
`;

export const Inner = styled.div`
  max-width: 1180px;
  margin: 0 auto;
`;

/* ── Breadcrumb ─────────────────────────────────────────────────────── */

export const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 22px;
  font-size: 0.82rem;
  color: rgba(46, 31, 15, 0.5);

  svg {
    flex: 0 0 auto;
  }
`;

export const BreadcrumbLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: inherit;
  text-decoration: none;

  &:hover {
    color: #3d6b4f;
    text-decoration: underline;
  }
`;

export const BreadcrumbCurrent = styled.span`
  color: #2e1f0f;
  font-weight: 700;
`;

export const BreadcrumbSep = styled.span`
  color: rgba(46, 31, 15, 0.28);
`;

/* ── Hero ───────────────────────────────────────────────────────────── */

export const HeroWrap = styled.div`
  text-align: center;
  max-width: 640px;
  margin: 0 auto 26px;
  animation: ${fadeUp} 0.5s ease both;
`;

export const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  color: #3d6b4f;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;

  svg {
    flex: 0 0 auto;
  }
`;

export const Title = styled.h1`
  margin: 0 0 10px;
  font-size: clamp(1.7rem, 3.6vw, 2.3rem);
  line-height: 1.15;
  letter-spacing: -0.02em;
  font-weight: 800;
  color: #2c5940;
`;

export const Desc = styled.p`
  margin: 0 auto;
  max-width: 520px;
  color: rgba(46, 31, 15, 0.65);
  font-size: 0.95rem;
  line-height: 1.6;
`;

/* ── Feature badges row ────────────────────────────────────────────── */

export const BadgeRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  max-width: 760px;
  margin: 22px auto 26px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

export const BadgeCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(46, 31, 15, 0.08);
  background: #fff;
  text-align: left;
`;

export const BadgeIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: rgba(61, 107, 79, 0.1);
  color: #3d6b4f;
`;

export const BadgeLabel = styled.span`
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.3;
  color: #2e1f0f;
`;

/* ── Category pills ────────────────────────────────────────────────── */

export const CategoryPillsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-bottom: 22px;
`;

export const CategoryPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 999px;
  border: 1.5px solid
    ${({ $active }) => ($active ? "#2c5940" : "rgba(46,31,15,0.14)")};
  background: ${({ $active }) => ($active ? "#2c5940" : "#ffffff")};
  color: ${({ $active }) => ($active ? "#fff" : "#3d3226")};
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 150ms ease, color 150ms ease, border-color 150ms ease;

  svg {
    flex: 0 0 auto;
    color: ${({ $active, $iconColor }) => ($active ? "#fff" : $iconColor || "#3d6b4f")};
  }

  &:hover {
    border-color: #2c5940;
  }

  &:focus-visible {
    outline: 3px solid rgba(61, 107, 79, 0.35);
    outline-offset: 2px;
  }
`;

/* ── Search + Filter row ───────────────────────────────────────────── */

export const SearchFilterRow = styled.div`
  display: flex;
  gap: 10px;
  max-width: 720px;
  margin: 0 auto 8px;
`;

export const SearchBox = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;

  svg.search-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(46, 31, 15, 0.4);
    pointer-events: none;
  }
`;

export const SearchInput = styled.input`
  width: 100%;
  height: 46px;
  padding: 0 14px 0 40px;
  border-radius: 999px;
  border: 1px solid rgba(46, 31, 15, 0.14);
  background: #fff;
  color: #2e1f0f;
  font-size: 0.88rem;

  &::placeholder {
    color: rgba(46, 31, 15, 0.38);
  }

  &:focus-visible {
    outline: none;
    border-color: #3d6b4f;
    box-shadow: 0 0 0 3px rgba(61, 107, 79, 0.16);
  }
`;

export const FilterToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex: 0 0 auto;
  padding: 0 18px;
  height: 46px;
  border-radius: 999px;
  border: 1px solid
    ${({ $active }) => ($active ? "#2c5940" : "rgba(46,31,15,0.14)")};
  background: ${({ $active }) => ($active ? "rgba(61,107,79,0.1)" : "#fff")};
  color: ${({ $active }) => ($active ? "#2c5940" : "#3d3226")};
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    border-color: #2c5940;
  }
`;

export const FilterPanel = styled.div`
  max-width: 720px;
  margin: 0 auto 22px;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid rgba(46, 31, 15, 0.08);
  background: #fcfbf8;
  animation: ${fadeUp} 0.25s ease both;
`;

export const FilterPanelTitle = styled.p`
  margin: 0 0 10px;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #3d3226;
`;

export const TagSuggestions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const TagChip = styled.button`
  padding: 7px 14px;
  border-radius: 999px;
  border: 1.5px solid
    ${({ $active }) => ($active ? "#2c5940" : "rgba(46,31,15,0.14)")};
  background: ${({ $active }) => ($active ? "#2c5940" : "#fff")};
  color: ${({ $active }) => ($active ? "#fff" : "#3d3226")};
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    border-color: #2c5940;
  }
`;

export const NoResultsBox = styled.div`
  max-width: 720px;
  margin: 0 auto 26px;
  padding: 40px 20px;
  text-align: center;
  color: rgba(46, 31, 15, 0.55);

  .icon {
    display: block;
    font-size: 2rem;
    margin-bottom: 10px;
  }
`;

/* ── Two-column layout ────────────────────────────────────────────── */

export const LayoutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  align-items: start;

  @media (min-width: 980px) {
    grid-template-columns: 270px 1fr;
  }
`;

export const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const SideCard = styled.div`
  padding: 18px;
  border: 1px solid rgba(46, 31, 15, 0.08);
  border-radius: 16px;
  background: #fcfbf8;
`;

export const SideCardHeader = styled.p`
  margin: 0 0 12px;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #3d6b4f;
`;

export const SideList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const SideItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 9px 10px;
  border: none;
  border-left: 3px solid
    ${({ $active }) => ($active ? "#2c5940" : "transparent")};
  border-radius: 6px;
  background: ${({ $active }) => ($active ? "rgba(61,107,79,0.08)" : "transparent")};
  color: ${({ $active }) => ($active ? "#2c5940" : "#3d3226")};
  font-size: 0.86rem;
  font-weight: ${({ $active }) => ($active ? 700 : 600)};
  text-align: left;
  cursor: pointer;

  &:hover {
    background: rgba(61, 107, 79, 0.06);
  }
`;

export const SideItemLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SideBadge = styled.span`
  flex: 0 0 auto;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(46, 31, 15, 0.07);
  color: rgba(46, 31, 15, 0.55);
  font-size: 0.68rem;
  font-weight: 700;
`;

export const ViewAllButton = styled.button`
  width: 100%;
  margin-top: 10px;
  padding: 10px;
  border-radius: 10px;
  border: 1px dashed rgba(61, 107, 79, 0.4);
  background: transparent;
  color: #2c5940;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover {
    background: rgba(61, 107, 79, 0.06);
  }
`;

export const WatchlistCard = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 16px;
  border: 1px solid rgba(46, 31, 15, 0.08);
  border-radius: 16px;
  background: #fff;
  text-align: left;
  cursor: pointer;

  &:hover {
    border-color: rgba(61, 107, 79, 0.35);
  }
`;

export const WatchlistIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${({ $active }) => ($active ? "#2c5940" : "rgba(61,107,79,0.1)")};
  color: ${({ $active }) => ($active ? "#fff" : "#3d6b4f")};
`;

export const WatchlistText = styled.span`
  flex: 1;
  min-width: 0;
`;

export const WatchlistTitle = styled.span`
  display: block;
  font-size: 0.87rem;
  font-weight: 800;
  color: #2e1f0f;
`;

export const WatchlistSubtitle = styled.span`
  display: block;
  margin-top: 2px;
  font-size: 0.76rem;
  color: rgba(46, 31, 15, 0.55);
`;

export const WatchlistChevron = styled.span`
  flex: 0 0 auto;
  color: rgba(46, 31, 15, 0.35);
`;

/* ── Main column: player + details ────────────────────────────────── */

export const MainCol = styled.div`
  min-width: 0;
`;

export const PlayerCard = styled.div`
  border: 1px solid rgba(46, 31, 15, 0.08);
  border-radius: 18px;
  overflow: hidden;
  background: #fff;
  margin-bottom: 18px;
`;

export const PlayerFrame = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  background: #111;

  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

export const PlayerThumbButton = styled.button`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: none;
  padding: 0;
  cursor: pointer;
  background: #000;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.82;
    display: block;
  }
`;

export const PlayerThumbPlayCircle = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  border: 2px solid rgba(255, 255, 255, 0.85);
  color: #fff;
  backdrop-filter: blur(1px);
`;

export const PlayerBody = styled.div`
  padding: 16px 18px 18px;
`;

export const PlayerTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
`;

export const PlayerTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 800;
  color: #2c5940;
`;

export const PlayerDuration = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(46, 31, 15, 0.06);
  color: rgba(46, 31, 15, 0.6);
  font-size: 0.76rem;
  font-weight: 700;
  white-space: nowrap;
`;

export const ConditionTagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const ConditionTag = styled.span`
  padding: 5px 12px;
  border-radius: 999px;
  background: rgba(201, 168, 76, 0.16);
  color: #8a6d1f;
  font-size: 0.76rem;
  font-weight: 700;
`;

export const EmptyPlayerState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  aspect-ratio: 16 / 9;
  color: rgba(46, 31, 15, 0.45);
  font-size: 0.9rem;

  .icon {
    font-size: 2rem;
  }
`;

/* ── Accordion detail box ─────────────────────────────────────────── */

export const DetailBox = styled.div`
  border-top: 1px solid rgba(46, 31, 15, 0.08);
  margin: 4px 18px 0;
`;

export const AccordionBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 15px 0;
  border: none;
  border-bottom: 1px solid rgba(46, 31, 15, 0.08);
  background: none;
  color: #2e1f0f;
  font-size: 0.92rem;
  font-weight: 700;
  text-align: left;
  cursor: pointer;

  .chevron {
    margin-left: auto;
    color: rgba(46, 31, 15, 0.4);
    transition: transform 150ms ease;
    transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
  }

  &:hover {
    color: #2c5940;
  }
`;

export const AccordionIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: ${({ $bg }) => $bg || "rgba(61,107,79,0.1)"};
  color: ${({ $color }) => $color || "#3d6b4f"};
`;

export const AccordionBody = styled.div`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? "1fr" : "0fr")};
  overflow: hidden;
  transition: grid-template-rows 200ms ease;
`;

export const AccordionInner = styled.div`
  min-height: 0;
  overflow: hidden;
  padding: ${({ $open }) => ($open ? "2px 0 16px 40px" : "0 0 0 40px")};
  color: rgba(46, 31, 15, 0.68);
  font-size: 0.88rem;
  line-height: 1.65;

  ol,
  ul {
    margin: 0;
    padding-left: 1.1em;
  }

  li {
    margin-bottom: 5px;
  }

  p {
    margin: 0;
  }
`;

/* ── "More Practices" carousel ────────────────────────────────────── */

export const MoreSection = styled.div`
  margin: 34px 0 26px;
`;

export const MoreHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

export const MoreTitle = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 800;
  color: #2c5940;
`;

export const ViewAllLink = styled.button`
  border: none;
  background: none;
  padding: 6px 14px;
  border-radius: 999px;
  color: #3d6b4f;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: rgba(61, 107, 79, 0.08);
  }
`;

export const MoreTrackWrap = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
  gap: 14px;
`;

export const MoreGrid = styled.div`
  flex: 1;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (min-width: 760px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

export const MoreCard = styled.button`
  display: block;
  width: 100%;
  padding: 0;
  border: 1px solid rgba(46, 31, 15, 0.08);
  border-radius: 14px;
  background: #fff;
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  animation: ${fadeUp} 0.35s ease both;
  animation-delay: ${({ $index = 0 }) => `${$index * 40}ms`};

  &:hover {
    border-color: rgba(61, 107, 79, 0.35);
  }
`;

export const MoreThumb = styled.div`
  position: relative;
  aspect-ratio: 16 / 10;
  background: #eee8df;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

export const MoreThumbDuration = styled.span`
  position: absolute;
  left: 6px;
  bottom: 6px;
  padding: 3px 6px;
  border-radius: 5px;
  background: rgba(0, 0, 0, 0.78);
  color: #fff;
  font-size: 0.62rem;
  font-weight: 800;
`;

export const MoreBookmark = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  padding: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  color: ${({ $active }) => ($active ? "#2c5940" : "#8a7a63")};
  font: inherit;
  cursor: pointer;
`;

export const MoreCardBody = styled.div`
  padding: 10px 12px 12px;
`;

export const MoreCardTitle = styled.p`
  margin: 0;
  font-size: 0.86rem;
  font-weight: 700;
  color: #22331f;
  line-height: 1.3;
`;

export const MoreCardSubtitle = styled.p`
  margin: 3px 0 0;
  font-size: 0.74rem;
  color: rgba(46, 31, 15, 0.5);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CarouselNavButton = styled.button`
  flex: 0 0 auto;
  align-self: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1px solid rgba(46, 31, 15, 0.14);
  background: #fff;
  color: #3d3226;
  cursor: pointer;

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    border-color: #2c5940;
    color: #2c5940;
  }
`;

/* ── Bottom CTA banner ─────────────────────────────────────────────── */

export const CTABanner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
  padding: 26px 28px;
  border-radius: 20px;
  background: rgba(61, 107, 79, 0.08);

  @media (min-width: 700px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

export const CTALeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

export const CTAIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  font-size: 1.4rem;
`;

export const CTATitle = styled.p`
  margin: 0;
  font-size: 1.02rem;
  font-weight: 800;
  color: #2c5940;
`;

export const CTASubtitle = styled.p`
  margin: 3px 0 0;
  font-size: 0.85rem;
  color: rgba(46, 31, 15, 0.6);
`;

export const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  padding: 12px 22px;
  border-radius: 999px;
  border: none;
  background: #2c5940;
  color: #fff;
  font-size: 0.86rem;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  transition: background 150ms ease;

  &:hover {
    background: #234a34;
  }
`;